/** Matches App.css @media (max-width: 768px) breakpoint for general mobile chrome */
export const MOBILE_BREAKPOINT_PX = 768;

/** Warehouse picking UI — phones and ~7–10" tablets (e.g. Samsung Tab A8 portrait/landscape) */
export const PICKING_TOUCH_MAX_PX = 1024;

/** Tablet band within picking touch — wider than phone, tuned for ~600px+ portrait tablets */
export const TABLET_MIN_PX = 600;

/** Long side cap for landscape tablets — excludes wide desktop windows with short height */
export const TABLET_LANDSCAPE_MAX_PX = 1366;

function viewportMinSide(width, height) {
  const w = width ?? (typeof window !== 'undefined' ? window.innerWidth : MOBILE_BREAKPOINT_PX + 1);
  const h = height ?? (typeof window !== 'undefined' ? window.innerHeight : PICKING_TOUCH_MAX_PX + 1);
  return Math.min(w, h);
}

function viewportMaxSide(width, height) {
  const w = width ?? (typeof window !== 'undefined' ? window.innerWidth : PICKING_TOUCH_MAX_PX + 1);
  const h = height ?? (typeof window !== 'undefined' ? window.innerHeight : PICKING_TOUCH_MAX_PX + 1);
  return Math.max(w, h);
}

/** Touch-first devices — coarse pointer or hover:none (phones/tablets, not mouse desktops) */
export function hasCoarsePointer() {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia('(pointer: coarse)').matches) return true;
  if (window.matchMedia('(hover: none)').matches) return true;
  return (navigator.maxTouchPoints ?? 0) > 0;
}

export function isMobileViewport(width = typeof window !== 'undefined' ? window.innerWidth : MOBILE_BREAKPOINT_PX + 1) {
  return width <= MOBILE_BREAKPOINT_PX;
}

/**
 * Phones and tablets in picking-only mode.
 * Portrait/narrow: width ≤1024. Landscape tablet: short side in tablet band, long side ≤1366, touch device.
 * Wide desktop windows with short height (e.g. 1920×900) stay out — min-side-only checks false-positive there.
 */
export function isPickingTouchViewport(
  width = typeof window !== 'undefined' ? window.innerWidth : PICKING_TOUCH_MAX_PX + 1,
  height = typeof window !== 'undefined' ? window.innerHeight : PICKING_TOUCH_MAX_PX + 1,
  { coarsePointer } = {}
) {
  const w = width;
  const h = height;
  const minSide = viewportMinSide(w, h);
  const maxSide = viewportMaxSide(w, h);
  const coarse = coarsePointer ?? hasCoarsePointer();

  if (w <= PICKING_TOUCH_MAX_PX) return true;

  if (
    coarse
    && minSide >= TABLET_MIN_PX
    && minSide <= PICKING_TOUCH_MAX_PX
    && maxSide <= TABLET_LANDSCAPE_MAX_PX
  ) {
    return true;
  }

  return false;
}

export function isTabletViewport(
  width = typeof window !== 'undefined' ? window.innerWidth : PICKING_TOUCH_MAX_PX + 1,
  height = typeof window !== 'undefined' ? window.innerHeight : PICKING_TOUCH_MAX_PX + 1,
  options = {}
) {
  if (!isPickingTouchViewport(width, height, options)) return false;
  return viewportMinSide(width, height) >= TABLET_MIN_PX;
}

/**
 * Compute a normalized (0–1) bounding box around map points with padding.
 * Centers the crop and clamps to the unit square.
 */
export function computeMapFitBounds(pointsNorm, {
  paddingRatio = 0.12,
  minSpan = 0.12
} = {}) {
  if (!pointsNorm?.length) return null;

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  pointsNorm.forEach(({ x, y }) => {
    if (x == null || y == null || Number.isNaN(x) || Number.isNaN(y)) return;
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
  });

  if (!Number.isFinite(minX)) return null;

  let width = Math.max(maxX - minX, 0.001);
  let height = Math.max(maxY - minY, 0.001);
  const padX = width * paddingRatio;
  const padY = height * paddingRatio;
  width += padX * 2;
  height += padY * 2;

  width = Math.max(width, minSpan);
  height = Math.max(height, minSpan);

  const cx = (minX + maxX) / 2;
  const cy = (minY + maxY) / 2;
  let x = cx - width / 2;
  let y = cy - height / 2;

  if (width > 1) {
    x = 0;
    width = 1;
  } else if (x < 0) {
    x = 0;
  } else if (x + width > 1) {
    x = 1 - width;
  }

  if (height > 1) {
    y = 0;
    height = 1;
  } else if (y < 0) {
    y = 0;
  } else if (y + height > 1) {
    y = 1 - height;
  }

  return { x, y, width, height };
}

/**
 * Build an SVG viewBox string from pixel-space bounds, centered with padding.
 */
export function computePixelCropViewBox(pointsPx, {
  canvasW,
  canvasH,
  paddingRatio = 0.12,
  minCropW = 0,
  minCropH = 0,
  markerPad = 0
} = {}) {
  if (!pointsPx?.length || !canvasW || !canvasH) {
    return `0 0 ${canvasW || 1} ${canvasH || 1}`;
  }

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  pointsPx.forEach(({ x, y }) => {
    if (x == null || y == null) return;
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
  });

  if (!Number.isFinite(minX)) {
    return `0 0 ${canvasW} ${canvasH}`;
  }

  minX -= markerPad;
  minY -= markerPad;
  maxX += markerPad;
  maxY += markerPad;

  let width = Math.max(maxX - minX, 1);
  let height = Math.max(maxY - minY, 1);
  const padX = width * paddingRatio;
  const padY = height * paddingRatio;
  width += padX * 2;
  height += padY * 2;

  width = Math.max(width, minCropW);
  height = Math.max(height, minCropH);

  const cx = (minX + maxX) / 2;
  const cy = (minY + maxY) / 2;
  let x = cx - width / 2;
  let y = cy - height / 2;

  if (width > canvasW) {
    x = 0;
    width = canvasW;
  } else if (x < 0) {
    x = 0;
  } else if (x + width > canvasW) {
    x = canvasW - width;
  }

  if (height > canvasH) {
    y = 0;
    height = canvasH;
  } else if (y < 0) {
    y = 0;
  } else if (y + height > canvasH) {
    y = canvasH - height;
  }

  return `${x.toFixed(1)} ${y.toFixed(1)} ${width.toFixed(1)} ${height.toFixed(1)}`;
}

/**
 * rAF-debounced container aspect measurement for ResizeObserver callbacks.
 * Skips updates when aspect ratio change is below threshold (avoids fitBounds loops).
 */
export function createAspectMeasure(node, onAspectChange, { threshold = 0.002 } = {}) {
  let rafId = null;
  let lastAspect = null;

  const measure = () => {
    if (!node) return;
    if (rafId) cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(() => {
      rafId = null;
      const { width, height } = node.getBoundingClientRect();
      if (width <= 0 || height <= 0) return;
      const aspect = width / height;
      if (lastAspect != null && Math.abs(aspect - lastAspect) / lastAspect < threshold) return;
      lastAspect = aspect;
      onAspectChange(aspect);
    });
  };

  measure.cancel = () => {
    if (rafId) cancelAnimationFrame(rafId);
    rafId = null;
  };

  return measure;
}
